import { FC, RefObject, useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import { useWorkerCommentsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { usePopper } from 'react-popper';
import { Transition } from '@headlessui/react';
import ViewEditCommentBox from 'react-scale/components/scalers/tasks/ThreadedInteraction/feedback/ViewEditCommentBox';
import { useCurrentEditor } from '@tiptap/react';
import { useEditorRendererContext } from '../../../components/editor/Editor';
import { ChatTaskContext } from '../../../contexts/ChatTaskContext';

type InlineCommentPopperProps = {
  anchorRef: RefObject<HTMLElement>;
  childId: string;
  enableEditorClickAway: boolean;
  isLeftPosition?: boolean;
};

// eslint-disable-next-line complexity
export const InlineCommentPopover: FC<InlineCommentPopperProps> = ({
  anchorRef,
  childId,
  enableEditorClickAway,
  isLeftPosition = false,
}) => {
  const taskContext = useContext(ChatTaskContext);
  const { canEditComments, areCommentsReadOnly, forceInlineCommentPosition, workerId } =
    taskContext;

  const canEdit = !!canEditComments;
  const readOnly = !!areCommentsReadOnly;
  const isInlinePosition = !!forceInlineCommentPosition;
  const { editor } = useCurrentEditor();
  const { editorRef } = useEditorRendererContext();

  const selectWorkerComment = useWorkerCommentsStore(state => state.selectWorkerComment);

  // All the unresolved inline worker comments for this step sorted by location
  const unresolvedSortedComments = useWorkerCommentsStore(state =>
    state.workerComments
      .filter(
        comment =>
          !_.isNil(comment.workerCommentId) &&
          comment.childId === childId &&
          !_.isNil(comment.locationStart) &&
          !_.isNil(comment.locationEnd) &&
          _.isNil(comment.feedbackResponse),
      )
      .sort((a, b) => a.locationStart! - b.locationStart!),
  );

  // Find the selected inline worker comment for this step
  const selectedWorkerComment = useWorkerCommentsStore(state => state.selectedWorkerComment);
  const selectedCommentIndex = unresolvedSortedComments.findIndex(
    comment => comment.workerCommentId === selectedWorkerComment,
  );
  const workerComment =
    selectedCommentIndex !== -1 ? unresolvedSortedComments.at(selectedCommentIndex) : undefined;

  // Determine the start location of the popper based on the worker comment
  const locationStart = workerComment?.locationStart ?? -1;
  const isCommentPosValid =
    editor && locationStart >= 0 && locationStart <= editor.view.state.doc.content.size;

  const start = isCommentPosValid ? editor.view.coordsAtPos(locationStart) : undefined;
  const verticalOffset =
    start && anchorRef.current ? start.top - anchorRef.current.getBoundingClientRect().top : 0;

  const [popperRef, setPopperRef] = useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(anchorRef.current, popperRef, {
    placement: isLeftPosition ? 'left-start' : 'right-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [verticalOffset, 28],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          mainAxis: false,
        },
      },
    ],
  });

  // Close the popper if the user clicks outside of the popper
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (
        popperRef &&
        !popperRef.contains(event.target) &&
        !popperRef.outerHTML.includes(event.target.outerHTML) &&
        (enableEditorClickAway ||
          (!editorRef?.current.outerHTML.includes(event.target.outerHTML) &&
            !editorRef?.current.contains(event.target)))
      ) {
        selectWorkerComment(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popperRef, editorRef, enableEditorClickAway, selectWorkerComment]);

  if (!editor || taskContext.enableUnifiedFeedback) return null;
  // Return null if the editor view isn't available or there is no valid, selected inline worker comment
  if (
    !workerComment ||
    _.isNil(workerComment.locationStart) ||
    _.isNil(workerComment.locationEnd)
  ) {
    return null;
  }

  const commentBox = (
    <ViewEditCommentBox
      workerComment={workerComment}
      selectedCommentIndex={selectedCommentIndex}
      unresolvedComments={unresolvedSortedComments}
      canEdit={canEdit && workerComment.author === workerId}
      readOnly={readOnly}
      isInlinePosition={isInlinePosition}
    />
  );

  // If the comment should be displayed in an inline position, return the comment box without the popper
  if (isInlinePosition && !workerComment) return null;
  if (isInlinePosition) return commentBox;

  return (
    <div ref={setPopperRef} style={styles.popper} {...attributes.popper} className="z-30">
      <Transition
        show={true}
        key={workerComment.workerCommentId}
        appear
        enter="transition-all duration-300"
        enterFrom="opacity-0 -translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition-all duration-300"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-1"
        as="div"
      >
        {commentBox}
      </Transition>
    </div>
  );
};
